import { CustomerIntelligenceScore } from 'src/app/models/customer-intelligence.model';

export interface ActivityBreakdownEntry {
  key: string;
  value: number;
  display: string;
  count?: number;
}

export function isIntelScore(raw: unknown): raw is CustomerIntelligenceScore {
  return (
    !!raw &&
    typeof raw === 'object' &&
    'display' in raw &&
    'percentage' in raw &&
    'max' in raw
  );
}

/** Unwraps `{ value }` metadata fields while leaving score objects unchanged. */
export function intelValue<T>(raw: unknown): T | null {
  if (raw == null) return null;
  if (isIntelScore(raw)) return raw as unknown as T;
  if (typeof raw === 'object' && 'value' in (raw as object)) {
    return (raw as { value: T }).value ?? null;
  }
  return raw as T;
}

export function intelInfo(raw: unknown): string | null {
  if (raw && typeof raw === 'object' && 'info' in raw) {
    const info = (raw as { info?: string | null }).info;
    return info?.trim() || null;
  }
  return null;
}

/** Visual indicator spec for UI (dots, stars, trend arrow, risk pills). */
export type IntelGraphVisualType = 'dots' | 'stars' | 'trend' | 'risk';

export interface IntelGraphVisual {
  type: IntelGraphVisualType;
  filled: number;
  total: number;
  trend?: 'up' | 'down' | 'flat';
  risk?: 'low' | 'medium' | 'high';
  /** Unranked segment (e.g. New Customer). */
  isNew?: boolean;
}

function clampLevel(value: number, max: number, slots = 5): number {
  if (max <= 0) return 0;
  const ratio = Math.min(1, Math.max(0, value / max));
  return Math.max(0, Math.min(slots, Math.round(ratio * slots)));
}

/** Builds a friendly visual indicator from score / metadata fields. */
export function intelGraphVisual(raw: unknown): IntelGraphVisual | null {
  if (raw == null) return null;

  if (isIntelScore(raw)) {
    return { type: 'dots', filled: clampLevel(raw.value, raw.max), total: 5 };
  }

  if (typeof raw !== 'object') return null;

  const obj = raw as Record<string, unknown>;
  const value = obj['value'];

  if (typeof value === 'string') {
    const text = value.toLowerCase();

    if (text === 'increasing' || text === 'up' || text === 'decreasing' || text === 'down' || text === 'stable' || text === 'flat') {
      const trend =
        text === 'increasing' || text === 'up' ? 'up' : text === 'decreasing' || text === 'down' ? 'down' : 'flat';
      return { type: 'trend', filled: 0, total: 0, trend };
    }

    if (text === 'high' || text === 'medium' || text === 'low') {
      const filled = text === 'high' ? 3 : text === 'medium' ? 2 : 1;
      return { type: 'risk', filled, total: 3, risk: text };
    }

    if ('segment_rank' in obj) {
      const rank = Number(obj['segment_rank']);
      if (!Number.isFinite(rank) || rank < 0) {
        return { type: 'stars', filled: 0, total: 5, isNew: true };
      }
      return { type: 'stars', filled: Math.max(0, Math.min(5, rank)), total: 5 };
    }
  }

  if (typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 5) {
    return { type: 'stars', filled: Math.max(0, Math.min(5, Math.round(value))), total: 5 };
  }

  return null;
}

/** activity_breakdown values may be wrapped, or contain { count, total_spend } entries. */
export function parseActivityBreakdown(breakdown: unknown): ActivityBreakdownEntry[] {
  const unwrapped = intelValue<Record<string, unknown>>(breakdown);
  if (!unwrapped || typeof unwrapped !== 'object') return [];

  const entries = Object.entries(unwrapped).map(([key, raw]) => {
    const value = coerceBreakdownNumber(raw);
    const count = coerceBreakdownCount(raw);
    const display = coerceBreakdownDisplay(raw, value, count);
    return { key, value, display, count };
  });

  return entries.sort((a, b) => b.value - a.value);
}

function coerceBreakdownCount(raw: unknown): number | undefined {
  if (raw && typeof raw === 'object' && typeof (raw as { count?: number }).count === 'number') {
    return (raw as { count: number }).count;
  }
  return undefined;
}

function coerceBreakdownNumber(raw: unknown): number {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string') {
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (raw && typeof raw === 'object') {
    const item = raw as Partial<CustomerIntelligenceScore> & {
      amount?: number;
      total?: number;
      total_spend?: number;
      count?: number;
    };
    if (typeof item.total_spend === 'number' && Number.isFinite(item.total_spend)) {
      return item.total_spend;
    }
    if (typeof item.value === 'number' && Number.isFinite(item.value)) return item.value;
    if (typeof item.amount === 'number' && Number.isFinite(item.amount)) return item.amount;
    if (typeof item.total === 'number' && Number.isFinite(item.total)) return item.total;
    if (typeof item.percentage === 'number' && Number.isFinite(item.percentage)) {
      return item.percentage;
    }
    if (typeof item.display === 'string') {
      const parsed = Number(item.display.replace(/[^0-9.-]/g, ''));
      return Number.isFinite(parsed) ? parsed : 0;
    }
  }
  return 0;
}

function formatInr(amount: number): string {
  return `₹${amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function coerceBreakdownDisplay(raw: unknown, fallback: number, count?: number): string {
  if (raw && typeof raw === 'object') {
    const item = raw as Partial<CustomerIntelligenceScore> & {
      amount?: number;
      total_spend?: number;
      count?: number;
    };

    if (typeof item.total_spend === 'number') {
      const spend = formatInr(item.total_spend);
      if (typeof item.count === 'number') {
        const visitLabel = item.count === 1 ? 'visit' : 'visits';
        return `${item.count} ${visitLabel} · ${spend}`;
      }
      return spend;
    }

    if (typeof item.display === 'string' && item.display.trim()) return item.display;
    if (typeof item.value === 'number') return String(item.value);
    if (typeof item.amount === 'number') return formatInr(item.amount);
  }
  if (typeof raw === 'number') return formatInr(raw);
  if (typeof raw === 'string') return raw;
  if (count != null) return `${count} visits · ${formatInr(fallback)}`;
  return formatInr(fallback);
}

/** Rewrites customer-facing copy into third-person text for CRM agents. */
export function toAgentInsightText(text: string | null | undefined): string | null {
  if (!text?.trim()) return null;

  let result = text.trim();

  result = result
    .replace(/^The customer\b/i, 'This customer')
    .replace(/^You are\b/i, 'This customer is')
    .replace(/^You're\b/i, 'This customer is')
    .replace(/^You have\b/i, 'This customer has')
    .replace(/^You've\b/i, 'This customer has')
    .replace(/^Your\b/i, 'Their');

  result = result
    .replace(/\bYou're\b/g, 'this customer is')
    .replace(/\bYou are\b/g, 'this customer is')
    .replace(/\bYou have\b/g, 'this customer has')
    .replace(/\bYou've\b/g, 'this customer has')
    .replace(/\bYour\b/g, 'Their')
    .replace(/\byour\b/g, 'their')
    .replace(/\byou\b/g, 'this customer');

  result = result.replace(/(\.\s+)(this customer)/g, (_, sep, phrase) => `${sep}${phrase.charAt(0).toUpperCase()}${phrase.slice(1)}`);

  return result;
}

/** Rewrites recommended actions into agent-facing suggestions. */
export function toAgentActionText(text: string | null | undefined): string | null {
  const normalized = toAgentInsightText(text);
  if (!normalized) return null;

  return normalized
    .replace(/^Redeem\b/i, 'Suggest redeeming')
    .replace(/^Consider redeeming\b/i, 'Consider suggesting they redeem')
    .replace(/^Send\b/i, 'Send')
    .replace(/^Encourage\b/i, 'Encourage');
}
